import type { WorkflowDefinitionError, WorkflowStepType } from 'src/gql/graphql';

/**
 * The step types the portal knows by name — the backend's `NodeType` — for what the editor draws
 * or treats specially: the trigger is always first and can't be added or deleted, an `if` has two
 * outputs to keep apart, and each type has its own icon and colours. What a step's fields are, and
 * which types exist, still comes from the catalog (`getWorkflowStepTypes`); `node.type` stays a
 * string, so a stored type outside this list renders as unknown instead of being lost.
 */
export const WorkflowNodeType = {
  Trigger: 'trigger',
  If: 'if',
  Say: 'say',
  CreateConversation: 'createConversation',
  CreateConversationForPhoneCall: 'createConversationForPhoneCall',
  CloseSmsConversation: 'closeSmsConversation',
  Play: 'play',
  SetVariables: 'setVariables',
  EnqueueForAgent: 'enqueueForAgent',
  ConnectCallTo: 'connectCallTo',
  Voicemail: 'voicemail',
  Callback: 'callback',
  CheckTimeAgainstSchedule: 'checkTimeAgainstSchedule',
} as const;

export type WorkflowNodeTypeValue = (typeof WorkflowNodeType)[keyof typeof WorkflowNodeType];

const NODE_TYPES = new Set<string>(Object.values(WorkflowNodeType));

export const isWorkflowNodeType = (type: string): type is WorkflowNodeTypeValue =>
  NODE_TYPES.has(type);

/** JSON with object keys sorted, so the same content always gives the same text. */
const sortedJson = (value: unknown): string => {
  if (Array.isArray(value)) return `[${value.map(sortedJson).join(',')}]`;
  if (value && typeof value === 'object') {
    return `{${Object.keys(value)
      .filter((key) => (value as Record<string, unknown>)[key] !== undefined)
      .sort()
      .map(
        (key) =>
          `${JSON.stringify(key)}:${sortedJson((value as Record<string, unknown>)[key])}`,
      )
      .join(',')}}`;
  }
  return JSON.stringify(value) ?? 'null';
};

/**
 * What a definition says, whatever order its keys come in: the list reads the stored JSONB back
 * with Postgres's key order, the save and create mutations echo the portal's own `JSON.stringify`.
 */
export const definitionContentKey = (definition: WorkflowDefinition | null): string =>
  definition ? sortedJson(definition) : '';

/** A step's settings, as stored in `node.data`. Its shape is described by the catalog's fields. */
export type WorkflowStepData = Record<string, unknown>;

/** Longest step name the backend accepts. */
export const STEP_NAME_MAX_LENGTH = 60;

export interface WorkflowDefinitionNode {
  id: string;
  /** `node.type` as stored. A type the catalog doesn't know is kept, rendered as unknown. */
  type: string;
  /** The title the step was given; the type's label stands in when absent. Never its id. */
  name?: string;
  data?: WorkflowStepData;
  position?: {
    x: number;
    y: number;
  };
}

export interface ParsedNodeShape {
  id: string;
  type: string;
  name?: unknown;
  data?: Record<string, unknown>;
  position?: { x: number; y: number };
}

export interface ParsedEdgeShape {
  id?: string;
  source: string;
  target: string;
  data?: {
    condition?: boolean;
  };
}

export interface WorkflowDefinitionEdge {
  id?: string;
  source: string;
  target: string;
  data?: {
    condition?: boolean;
  };
}

export interface WorkflowDefinition {
  nodes: WorkflowDefinitionNode[];
  edges: WorkflowDefinitionEdge[];
}

export interface ParsedWorkflowDefinition {
  definition: WorkflowDefinition | null;
  error: string | null;
}

export const isWorkflowDefinitionShape = (
  definition: unknown,
): definition is { nodes: unknown[]; edges: unknown[] } => {
  if (typeof definition !== 'object' || definition === null) {
    return false;
  }

  const candidate = definition as { nodes?: unknown; edges?: unknown };
  return Array.isArray(candidate.nodes) && Array.isArray(candidate.edges);
};

export const isValidNodeShape = (node: unknown): node is ParsedNodeShape => {
  if (typeof node !== 'object' || node === null) {
    return false;
  }
  const candidate = node as { id?: unknown; type?: unknown };
  return typeof candidate.id === 'string' && typeof candidate.type === 'string';
};

export const isValidEdgeShape = (edge: unknown): edge is ParsedEdgeShape => {
  if (typeof edge !== 'object' || edge === null) {
    return false;
  }
  const candidate = edge as { source?: unknown; target?: unknown };
  return typeof candidate.source === 'string' && typeof candidate.target === 'string';
};

/** The step types the backend describes, indexed for lookups by `node.type`. */
export interface WorkflowStepCatalog {
  stepTypes: WorkflowStepType[];
  byType: ReadonlyMap<string, WorkflowStepType>;
}

export const toWorkflowStepCatalog = (stepTypes: WorkflowStepType[]): WorkflowStepCatalog => ({
  stepTypes,
  byType: new Map(stepTypes.map((stepType) => [stepType.type, stepType])),
});

/** The step types + offers on a workflow of `triggerType`: every non-trigger type that runs on it. */
export const getAddableStepTypes = (
  catalog: WorkflowStepCatalog,
  triggerType: string,
): WorkflowStepType[] =>
  catalog.stepTypes.filter(
    (stepType) => !stepType.isTrigger && stepType.triggerTypes.includes(triggerType),
  );

/**
 * A problem with a step, from the portal's own check or from a save. One shape for both, so a
 * problem lands in the same place on the canvas wherever it came from.
 */
export type WorkflowStepProblem = Omit<WorkflowDefinitionError, '__typename'>;

/**
 * Where a new step goes: on an existing connection (between its two steps), or at an output that
 * has nothing after it yet — the end of a path, or an `if` output left empty.
 */
export type WorkflowInsertionPoint =
  | { kind: 'edge'; edgeId: string }
  | { kind: 'output'; sourceId: string; condition?: boolean };
